import { message, Modal } from "ant-design-vue";
import { h } from "vue";
import { getVersionInfo } from "@/api/other";

// 定义类型
interface UpdateInfo {
  currentVersion: string;
  newVersion: string;
  size: string;
  isForce?: boolean;
  changelog?: string;
}

interface UpdateOptions {
  onUpdate?: () => void;
  onCancel?: () => void;
  onViewDetail?: () => void;
}

export function useUpdate() {
  // 统一格式化更新大小：无论后端返回 "172.01" / "172.01MB" / "172.01 MB" / "172.01MB MB"
  // 最终都只展示一次 "MB"
  const formatUpdateSize = (rawSize: string) => {
    if (!rawSize) return "--";
    // 提取第一个数值（允许小数）
    const match = String(rawSize).match(/(\d+(?:\.\d+)?)/);
    if (!match) return String(rawSize).trim();
    return `${match[1]}MB`;
  };

  // 添加自定义样式
  const initStyles = () => {
    const styleId = "update-dialog-styles";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      .update-dialog .ant-modal-confirm-body {
        display: flex;
        align-items: flex-start;
      }

      .update-dialog .ant-modal-confirm-body > .anticon {
        margin-right: 16px;
        margin-top: 2px;
      }

      .update-dialog .ant-modal-confirm-title {
        font-size: 14px!important;
        font-weight: 700!important;
        color: #333;
      }

      .update-dialog .ant-modal-confirm-content {
        margin-top: 8px;
        color: #333;
      }

      .force-update-dialog .ant-modal-confirm-btns {
        justify-content: flex-end;
      }

   
      .update-dialog .ant-modal-confirm-btns {
      position: relative;
      margin-top: 24px;
      padding-top: 16px;
    }

    .update-dialog .ant-modal-confirm-btns::before {
      content: '';
      position: absolute;
      top: 0;
      left: -24px;  /* 弹窗默认左右padding为24px */
      right: -24px;
      height: 1px;
      background-color: #f0f0f0;
    }
      .update-dialog .ant-btn {
      outline: none !important;
    }
    `;
    document.head.appendChild(style);
  };
  const getSizeByPlatform = (
    platform: string,
    {
      sizeWin,
      sizeMac,
      sizeLinux,
    }: { sizeWin: string; sizeMac: string; sizeLinux: string }
  ) => {
    switch (platform.toLowerCase()) {
      case "win32":
        return sizeWin;
      case "darwin":
        return sizeMac;
      case "linux":
        return sizeLinux;
      default:
        return sizeWin; // Default to Windows size if platform is unknown
    }
  };

  // 生成对话框内容
  const generateContent = (info: UpdateInfo, _options: UpdateOptions) => {
    return h("div", { style: { marginTop: "12px" } }, [
      h(
        "p",
        {},
        `您当前使用的客户端版本为 ${info.currentVersion}，${
          info.isForce ? "已无法正常使用" : "已不是最新版本"
        }。建议更新至最新版本${info.newVersion}，以获得更好的使用体验。`
      ),
      h("p", { style: { marginTop: "12px" } }, [
        h(
          "span",
          { style: { color: "#666" } },
          `更新大小：${formatUpdateSize(info.size)}`
        ),
      ]),
      h("p", { style: { marginTop: "4px" } }, [
        h("span", { style: { color: "#666" } }, `更新内容：`),
        h(
          "a",
          {
            style: { color: "#5B69E6" },
            onClick: (e: Event) => {
              e.preventDefault();
              // _options.onViewDetail?.();
              window.open(
                "https://help.yeez.tech/docs/2025-ke-hu-duan-geng-xin-ji-lu"
              );
            },
          },
          "查看详情"
        ),
      ]),
    ]);
  };

  // 显示更新对话框
  const showUpdateDialog = (info: UpdateInfo, options: UpdateOptions = {}) => {
    initStyles();

    // 创建 Modal 配置
    const modalConfig: Parameters<typeof Modal.confirm>[0] = {
      title: "更新提示",
      icon: null,
      width: "552px",
      content: generateContent(info, options),
      okText: info.isForce ? "立即更新" : "立即更新",
      cancelText: "以后再说",
      okCancel: !info.isForce,
      class: `update-dialog ${info.isForce ? "force-update-dialog" : ""}`,
      closable: !info.isForce,
      keyboard: !info.isForce,
      maskClosable: !info.isForce,
      onOk() {
        // options.onUpdate?.();
        window.electronAPI.checkAutoUpdate();
      },
      onCancel() {
        options.onCancel?.();
      },
    };

    Modal.confirm(modalConfig);
  };

  // 检查更新的主函数
  const checkUpdate = async (showLatestMessage = false) => {
    try {
      const version = await window.electronAPI.getVersion();
      const platform = await window.electronAPI.platform;
      console.log("version:", version);
      console.log("platform:", platform);
      const updateInfo = await getVersionInfo(version);
      console.log("updateInfo:", updateInfo);
      if (updateInfo.needUpdate) {
        const size = getSizeByPlatform(platform, {
          sizeWin: updateInfo.sizeWin,
          sizeMac: updateInfo.sizeMac,
          sizeLinux: updateInfo.sizeLinux,
        });
        showUpdateDialog(
          {
            currentVersion: version,
            newVersion: updateInfo.versionNo.replace(/^V/i, ""),
            size: size,
            isForce: updateInfo.needForceUpdate,
          },
          {
            onUpdate: () => {
              window.electronAPI.vueSend("startUpdate");
            },
            onCancel: () => {
              window.electronAPI.vueSend("cancelUpdate");
            },
          }
        );
      } else {
        if (showLatestMessage) {
          message.success("当前已是最新版本");
        }
      }
    } catch (error) {
      console.error("检查更新失败:", error);
    }
  };

  return {
    checkUpdate,
    showUpdateDialog,
  };
}
